import { Dialog } from '@base-ui/react/dialog';
import { ChevronLeft, ChevronRight, Columns2, Download, Share2, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { useBlobUrl } from '../app/useBlobUrl.ts';
import { canShareFiles, downloadBlob, shareFiles } from '../export/save.ts';
import { timeAgo } from '../lib/format.ts';
import { RenderFeedback } from '../renders/RenderFeedback.tsx';
import { ask, errorMessage, toast } from '../state/feedback.ts';
import { storage } from '../storage/index.ts';
import { Button } from './Button.tsx';
import { IconButton } from './IconButton.tsx';

export interface ViewerItem {
  id: string;
  kind: 'ai' | 'export';
  uri: string;
  title: string;
  createdAt: number;
  fileName: string;
  /** Small technical details (AI results: model and prompt version). */
  details?: string;
}

/** Full-screen viewer with hold-to-compare, Download / Share / Delete (docs/spec/06 §4.2). */
export function FileViewer({
  items,
  index,
  onIndex,
  onClose,
  photoUri,
  onDelete,
}: {
  items: ViewerItem[];
  index: number | null;
  onIndex: (i: number) => void;
  onClose: () => void;
  photoUri: string;
  onDelete: (item: ViewerItem) => Promise<void>;
}) {
  const [comparing, setComparing] = useState(false);
  const item = index !== null ? items[index] : undefined;
  const url = useBlobUrl(item?.uri);
  const original = useBlobUrl(photoUri);
  const n = items.length;

  const fileFor = async (it: ViewerItem): Promise<File> => {
    const blob = await storage().blobs.getByUri(it.uri);
    if (!blob) throw new Error('That file is missing.');
    return new File([blob], it.fileName, { type: blob.type || 'image/jpeg' });
  };

  return (
    <Dialog.Root open={!!item} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Popup
          className="fixed inset-0 z-50 flex flex-col bg-black outline-none"
          style={{ paddingTop: 'var(--safe-top)', paddingBottom: 'var(--safe-bottom)' }}
        >
          {item ? (
            <>
              <div className="flex items-center gap-2 p-2">
                <IconButton icon={X} label="Close" onClick={onClose} />
                <div className="flex min-w-0 flex-1 flex-col">
                  <Dialog.Title className="t-label truncate">{item.title}</Dialog.Title>
                  <span className="t-caption text-dim">
                    {index! + 1} / {n} · {timeAgo(item.createdAt)}
                    {item.details ? (
                      <span data-testid="viewer-details" className="text-muted">
                        {' '}
                        · {item.details}
                      </span>
                    ) : null}
                  </span>
                </div>
                {item.kind === 'ai' ? <RenderFeedback key={item.id} aiResultId={item.id} /> : null}
              </div>
              <div className="relative flex min-h-0 flex-1 items-center justify-center">
                {(comparing ? original : url) ? (
                  <img
                    src={(comparing ? original : url)!}
                    alt={comparing ? 'Original photo' : item.title}
                    className="max-h-full max-w-full object-contain"
                  />
                ) : null}
                {comparing ? (
                  <span className="glass t-overline absolute top-3 left-3 rounded-full px-3 py-1">Original</span>
                ) : null}
                {n > 1 ? (
                  <>
                    <IconButton
                      icon={ChevronLeft}
                      label="Previous"
                      tone="glass"
                      className="absolute left-3"
                      onClick={() => onIndex((index! - 1 + n) % n)}
                    />
                    <IconButton
                      icon={ChevronRight}
                      label="Next"
                      tone="glass"
                      className="absolute right-3"
                      onClick={() => onIndex((index! + 1) % n)}
                    />
                  </>
                ) : null}
              </div>
              <div className="mx-auto flex w-full max-w-[560px] flex-wrap items-center justify-center gap-2 p-3">
                <Button
                  icon={Columns2}
                  onPointerDown={() => setComparing(true)}
                  onPointerUp={() => setComparing(false)}
                  onPointerLeave={() => setComparing(false)}
                  onKeyDown={(e) => e.key === ' ' && setComparing(true)}
                  onKeyUp={() => setComparing(false)}
                >
                  Hold to compare
                </Button>
                <Button
                  variant="primary"
                  icon={Download}
                  onClick={async () => {
                    try {
                      const f = await fileFor(item);
                      downloadBlob(f, f.name);
                      toast('Downloaded', 'success');
                    } catch (e) {
                      toast(errorMessage(e, 'Could not download that file.'), 'error');
                    }
                  }}
                >
                  Download
                </Button>
                {canShareFiles() ? (
                  <Button
                    icon={Share2}
                    onClick={async () => {
                      try {
                        await shareFiles([await fileFor(item)]);
                      } catch (e) {
                        toast(errorMessage(e, 'Sharing is not available on this device.'), 'error');
                      }
                    }}
                  >
                    Share…
                  </Button>
                ) : null}
                <IconButton
                  icon={Trash2}
                  label="Delete"
                  onClick={async () => {
                    const what = item.kind === 'ai' ? 'render' : 'export';
                    const ok = await ask({
                      title: `Delete this ${what}?`,
                      message: 'It will be removed from this project. Copies already saved to Photos stay there.',
                      actions: [
                        { label: 'Delete', value: true, variant: 'danger' },
                        { label: 'Cancel', value: false, variant: 'ghost' },
                      ],
                    });
                    if (ok) await onDelete(item);
                  }}
                />
              </div>
            </>
          ) : null}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
